import { expectArray, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const STUDENT_STATUSES = ["active", "inactive"];
export const FAMILY_ACCOUNT_STATUSES = ["active", "inactive"];
export const ENROLLMENT_TYPES = ["trial", "regular"];
export const ENROLLMENT_STATUSES = [
    "pendingStart",
    "active",
    "paused",
    "ended",
    "cancelled"
];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function normalizeStudentNamePart(value) {
    if (typeof value !== "string") {
        return null;
    }
    const normalized = value.trim().replace(/\s+/g, " ");
    return normalized.length > 0 ? normalized : null;
}
export function composeStudentDisplayNameFromParts(familyName, givenName) {
    const normalizedFamilyName = normalizeStudentNamePart(familyName);
    const normalizedGivenName = normalizeStudentNamePart(givenName);
    if (!normalizedFamilyName || !normalizedGivenName) {
        return null;
    }
    return `${normalizedFamilyName} ${normalizedGivenName}`;
}
export function composeFamilyAccountDisplayNameFromStudentFamilyName(familyName) {
    const normalizedFamilyName = normalizeStudentNamePart(familyName);
    return normalizedFamilyName ? `${normalizedFamilyName}様` : null;
}
export function parseStudentStatus(value, path = "studentStatus") {
    return parseStringEnum(value, STUDENT_STATUSES, path);
}
export function parseFamilyAccountStatus(value, path = "familyAccountStatus") {
    return parseStringEnum(value, FAMILY_ACCOUNT_STATUSES, path);
}
export function parseEnrollmentType(value, path = "enrollmentType") {
    return parseStringEnum(value, ENROLLMENT_TYPES, path);
}
export function parseEnrollmentStatus(value, path = "enrollmentStatus") {
    return parseStringEnum(value, ENROLLMENT_STATUSES, path);
}
export function parseTrialPolicySnapshot(value, path = "trialPolicySnapshot") {
    const record = expectRecord(value, path);
    return {
        trialPolicyId: expectString(record.trialPolicyId, `${path}.trialPolicyId`)
    };
}
export function parseAdminStudent(value, path = "adminStudent") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        memberCode: expectString(record.memberCode, `${path}.memberCode`),
        familyName: expectOptionalString(record.familyName, `${path}.familyName`),
        givenName: expectOptionalString(record.givenName, `${path}.givenName`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        birthDate: expectString(record.birthDate, `${path}.birthDate`),
        status: parseStudentStatus(record.status, `${path}.status`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        familyAccountDisplayName: expectOptionalString(record.familyAccountDisplayName, `${path}.familyAccountDisplayName`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminStudentsResponse(value, path = "adminStudentsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminStudent, `${path}.items`)
    };
}
export function parseCreateAdminStudentRequest(value, path = "createAdminStudentRequest") {
    const record = expectRecord(value, path);
    const createFamilyAccountRecord = record.createFamilyAccount === undefined || record.createFamilyAccount === null
        ? null
        : expectRecord(record.createFamilyAccount, `${path}.createFamilyAccount`);
    return {
        displayName: expectString(record.displayName, `${path}.displayName`),
        familyName: expectOptionalString(record.familyName, `${path}.familyName`),
        givenName: expectOptionalString(record.givenName, `${path}.givenName`),
        displayNameKana: expectOptionalString(record.displayNameKana, `${path}.displayNameKana`),
        birthDate: expectString(record.birthDate, `${path}.birthDate`),
        status: parseStudentStatus(record.status, `${path}.status`),
        familyAccountId: expectOptionalString(record.familyAccountId, `${path}.familyAccountId`),
        createFamilyAccount: createFamilyAccountRecord
            ? {
                displayName: expectString(createFamilyAccountRecord.displayName, `${path}.createFamilyAccount.displayName`),
                status: createFamilyAccountRecord.status === undefined
                    ? null
                    : parseFamilyAccountStatus(createFamilyAccountRecord.status, `${path}.createFamilyAccount.status`)
            }
            : null,
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
export function parseUpdateAdminStudentRequest(value, path = "updateAdminStudentRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "familyName")) {
        parsed.familyName = expectOptionalString(record.familyName, `${path}.familyName`);
    }
    if (hasOwn(record, "givenName")) {
        parsed.givenName = expectOptionalString(record.givenName, `${path}.givenName`);
    }
    if (hasOwn(record, "displayNameKana")) {
        parsed.displayNameKana = expectOptionalString(record.displayNameKana, `${path}.displayNameKana`);
    }
    if (hasOwn(record, "birthDate")) {
        parsed.birthDate = expectString(record.birthDate, `${path}.birthDate`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseStudentStatus(record.status, `${path}.status`);
    }
    if (hasOwn(record, "familyAccountId")) {
        parsed.familyAccountId = expectOptionalString(record.familyAccountId, `${path}.familyAccountId`);
    }
    if (hasOwn(record, "notesInternal")) {
        parsed.notesInternal = expectOptionalString(record.notesInternal, `${path}.notesInternal`);
    }
    return parsed;
}
export function parseFamilyAccount(value, path = "familyAccount") {
    const record = expectRecord(value, path);
    return {
        familyAccountId: expectString(record.familyAccountId, `${path}.familyAccountId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        primaryContactPlatformUserId: expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`),
        primaryContactDisplayNameSnapshot: expectOptionalString(record.primaryContactDisplayNameSnapshot, `${path}.primaryContactDisplayNameSnapshot`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseFamilyAccountsResponse(value, path = "familyAccountsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseFamilyAccount, `${path}.items`)
    };
}
export function parseCreateFamilyAccountRequest(value, path = "createFamilyAccountRequest") {
    const record = expectRecord(value, path);
    return {
        displayName: expectString(record.displayName, `${path}.displayName`),
        primaryContactPlatformUserId: expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`),
        status: parseFamilyAccountStatus(record.status, `${path}.status`)
    };
}
export function parseUpdateFamilyAccountRequest(value, path = "updateFamilyAccountRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "primaryContactPlatformUserId")) {
        parsed.primaryContactPlatformUserId = expectOptionalString(record.primaryContactPlatformUserId, `${path}.primaryContactPlatformUserId`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseFamilyAccountStatus(record.status, `${path}.status`);
    }
    return parsed;
}
export function parseEnrollment(value, path = "enrollment") {
    const record = expectRecord(value, path);
    return {
        enrollmentId: expectString(record.enrollmentId, `${path}.enrollmentId`),
        revision: expectNumber(record.revision, `${path}.revision`),
        studentId: expectString(record.studentId, `${path}.studentId`),
        studentDisplayName: expectString(record.studentDisplayName, `${path}.studentDisplayName`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        lessonTitle: expectString(record.lessonTitle, `${path}.lessonTitle`),
        enrollmentType: parseEnrollmentType(record.enrollmentType, `${path}.enrollmentType`),
        status: parseEnrollmentStatus(record.status, `${path}.status`),
        joinDate: expectString(record.joinDate, `${path}.joinDate`),
        leaveDate: expectOptionalString(record.leaveDate, `${path}.leaveDate`),
        trialPolicySnapshot: expectNullable(record.trialPolicySnapshot, parseTrialPolicySnapshot, `${path}.trialPolicySnapshot`),
        convertedFromEnrollmentId: expectOptionalString(record.convertedFromEnrollmentId, `${path}.convertedFromEnrollmentId`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseEnrollmentsResponse(value, path = "enrollmentsResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseEnrollment, `${path}.items`)
    };
}
export function parseCreateEnrollmentRequest(value, path = "createEnrollmentRequest") {
    const record = expectRecord(value, path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        lessonId: expectString(record.lessonId, `${path}.lessonId`),
        enrollmentType: parseEnrollmentType(record.enrollmentType, `${path}.enrollmentType`),
        status: expectNullable(record.status, parseEnrollmentStatus, `${path}.status`),
        joinDate: expectString(record.joinDate, `${path}.joinDate`),
        leaveDate: expectOptionalString(record.leaveDate, `${path}.leaveDate`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
export function parseUpdateEnrollmentRequest(value, path = "updateEnrollmentRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "status")) {
        parsed.status = parseEnrollmentStatus(record.status, `${path}.status`);
    }
    if (hasOwn(record, "joinDate")) {
        parsed.joinDate = expectString(record.joinDate, `${path}.joinDate`);
    }
    if (hasOwn(record, "leaveDate")) {
        parsed.leaveDate = expectOptionalString(record.leaveDate, `${path}.leaveDate`);
    }
    if (hasOwn(record, "notesInternal")) {
        parsed.notesInternal = expectOptionalString(record.notesInternal, `${path}.notesInternal`);
    }
    return parsed;
}
export function parseTransitionEnrollmentStatusRequest(value, path = "transitionEnrollmentStatusRequest") {
    const record = expectRecord(value, path);
    return {
        toStatus: parseEnrollmentStatus(record.toStatus, `${path}.toStatus`)
    };
}
export function parseConvertEnrollmentToRegularRequest(value, path = "convertEnrollmentToRegularRequest") {
    const record = expectRecord(value, path);
    return {
        joinDate: expectOptionalString(record.joinDate, `${path}.joinDate`),
        notesInternal: expectOptionalString(record.notesInternal, `${path}.notesInternal`)
    };
}
